import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/common/Navbar";
import CustomSidebar from "../../components/common/Sidebar/Sidebar";
import Loader from "../../components/common/Loader";
import { getOnboardingStatus, getFundraiserProfile } from "../../services/fundraiserService";
import { clearAuth } from "../../redux/slices/authSlice";

const FeaturedForm = ({ element }) => {
    const [isSideBarOpen, setisSideBarOpen] = useState(true);
    const [isCheckingStatus, setIsCheckingStatus] = useState(true);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const token = useSelector((state) => state?.authReducer?.tokenData?.token);
    const user = useSelector((state) => state?.authReducer?.tokenData?.user);
    const userRole = user?.role?.toLowerCase();

    const clearFundraiserSessionState = () => {
        sessionStorage.removeItem("showAccountPendingModal");
        sessionStorage.removeItem("fundraiser_stripe_complete");
        sessionStorage.removeItem("fundraiser_block_status");
        sessionStorage.removeItem("fundraiser_verified");
        sessionStorage.removeItem("skip_auto_redirect");
    };

    useEffect(() => {
        // Only check for fundraisers
        if (userRole === "fundraiser" && token) {
            // Check if we just verified during login - skip re-checking
            const alreadyVerified = sessionStorage.getItem('fundraiser_verified') === 'true';

            if (alreadyVerified) {
                // Already verified in login flow, allow dashboard access immediately
                setIsCheckingStatus(false);
                return;
            }

            const checkStatus = async () => {
                try {
                    const fundraiserProfileResponse = await getFundraiserProfile();
                    const fundraiserProfile = fundraiserProfileResponse?.data;
                    const profileStatus = String(fundraiserProfile?.status || "").toLowerCase();
                    const isApproved = profileStatus === "approved" || profileStatus === "live";

                    if (!isApproved) {
                        clearFundraiserSessionState();
                        dispatch(clearAuth());

                        let message = "Your account is pending admin approval. Please wait for approval.";
                        let showPendingModal = true;
                        let blockStatus = "pending";

                        if (profileStatus === "disabled") {
                            message = "Your account is disabled. Please contact support.";
                            showPendingModal = false;
                            blockStatus = "disabled";
                        } else if (profileStatus === "deleted") {
                            message = "Your account have been deleted. Please Contact Support";
                            showPendingModal = false;
                            blockStatus = "deleted";
                        } else if (profileStatus === "rejected") {
                            message = "Your account has been rejected. Please contact support.";
                            blockStatus = "rejected";
                        } else if (profileStatus === "declined") {
                            message = "Your account has been declined. Please contact support.";
                            blockStatus = "declined";
                        }

                        sessionStorage.setItem("fundraiser_block_status", blockStatus);
                        if (showPendingModal) {
                            sessionStorage.setItem("showAccountPendingModal", "true");
                        }

                        navigate("/fundraiser/account-login", {
                            replace: true,
                            state: { message, showAccountPendingModal: showPendingModal },
                        });
                        return;
                    }

                    const statusPromise = await dispatch(getOnboardingStatus()).unwrap();
                    const detailsSubmitted = statusPromise?.data?.detailsSubmitted === true;
                    const chargesEnabled = statusPromise?.data?.chargesEnabled === true;

                    // If Stripe is not complete, redirect to welcome
                    if (!detailsSubmitted || !chargesEnabled) {
                        if (detailsSubmitted && !chargesEnabled) {
                            navigate("/complete-setup", { replace: true });
                        } else {
                            navigate("/fundraiser/welcome", { replace: true });
                        }
                        return;
                    }

                    // Stripe is complete, allow dashboard access
                    setIsCheckingStatus(false);
                } catch (error) {
                    clearFundraiserSessionState();
                    dispatch(clearAuth());
                    navigate("/fundraiser/account-login", {
                        replace: true,
                        state: { message: "Failed to verify account status. Please try again." },
                    });
                }
            };

            checkStatus();
        } else {
            // For non-fundraisers or no token, show immediately
            setIsCheckingStatus(false);
        }
    }, [userRole, token, dispatch, navigate]);

    return (
        <div className="flex h-full w-full fixed">
            <CustomSidebar isSideBarOpen={isSideBarOpen} setisSideBarOpen={setisSideBarOpen} />
            <Navbar
                isSideBarOpen={isSideBarOpen}
                setisSideBarOpen={setisSideBarOpen}
                element={isCheckingStatus && userRole === "fundraiser" ? (
                    <Loader fullscreen={false} className="w-full h-[75vh]" />
                ) : element}
            />
        </div>
    )
}

export default FeaturedForm;
